import { FEAT, HEROES, NIGHTMARES, SKILLS, formFx, type SkillDef } from '@somnhold/content';
import { cosA, dist2, iatan2, idiv, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngChance } from '../math/rng.ts';
import type { BattleState, Bot, Enemy, Hero, Projectile, UqDue, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, ev, isFlying, targetable } from './combat.ts';
import { featPieceTest, featPieceWatch, featUse } from './feats.ts';
import { formFinal } from './forms.ts';
import { bigOne, due, heroById, pieceStun } from './panoply.ts';
import { enemiesInLine, poison, shove } from './skills.ts';
import { isBoss, launch, stunFor } from './towers.ts';
import { fx, hasUq, uqBp } from './uniques.ts';

/**
 * The ranger's panoply in a fight (docs/PANOPLIA.md §10), as battle/panoply.ts does the warrior's: each piece changes
 * how its skill works, what comes later goes in the queue, and what its feats count is counted here. What a piece
 * keeps on a hero goes in `h.uqT`, on a nightmare in `e.uqE`, by key: absent on everyone who doesn't wear one.
 */

const kept = (h: Hero): Record<string, number> => (h.uqT ??= {});
const marks = (e: Enemy): Record<string, number> => (e.uqE ??= {});

/** The nearest standing nightmare to a point within `r`, but those in `skip` (by id on ties). */
const nearestBut = (s: BattleState, x: number, y: number, r: number, skip: readonly number[]): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const o of s.enemies) {
    if (o.hp <= 0 || skip.includes(o.id) || !targetable(s, o) || o.kind === 'boneWall' || !withinRange(o.x, o.y, x, y, r)) continue;
    const d = dist2(o.x, o.y, x, y);
    if (d < bd || (d === bd && best && o.id < best.id)) {
      bd = d;
      best = o;
    }
  }
  return best;
};

/** Pegan menos (§8) for `ticks`: the big ones and the elites, at half of it. */
const weaken = (o: Enemy, bp: number, ticks: number): void => {
  const b = o.elite || bigOne(o) ? 10000 - idiv(10000 - bp, 2) : bp;
  if ((o.blind ?? 0) < ticks) o.blind = ticks;
  o.blindBp = imin(o.blindBp ?? 10000, b);
};

/** A flyer a piece may bring down: never a boss, never the dragon. */
const groundable = (e: Enemy): boolean => (isFlying(e) || (e.grounded ?? 0) > 0) && e.kind !== 'dragon' && !bigOne(e) && !isBoss(e);

/* ---------- Arco del cazador (Disparo múltiple) ---------- */

/** A volley with the hunter's bow: its id, carried by its arrows (0 without the bow). */
export const hunterVolley = (s: BattleState, h: Hero): number => {
  if (!hasUq(h, 'hunterBow')) return 0;
  const q = kept(h);
  q.vol = s.nextId++;
  q.volK = 0;
  return q.vol;
};

/** A volley arrow landed: one that killed jumps once (next tick); one whose target was gone falls again (Segunda pasada). */
export const arrowLanded = (s: BattleState, p: Projectile, e: Enemy | null): void => {
  const h = p.hero === undefined ? s.heroes.find((o) => o.pid === p.pid && hasUq(o, 'hunterBow')) : heroById(s, p.hero);
  if (!h) return;
  const vol = p.uq as number;
  if (!e) {
    if (vol > 0 && formFinal(h, 'hunterBow')) due(s, { at: s.tick + (formFx('hunterBow', 'final').after as number), k: 'arrowAgain', hid: h.id, x: p.x, y: p.y, n: [p.damage, vol] });
    return;
  }
  if (e.hp > 0) return;
  volleyKill(s, h, vol < 0 ? -vol : vol);
  if (vol > 0) due(s, { at: s.tick + 1, k: 'arrowJump', hid: h.id, x: e.x, y: e.y, n: [p.damage, vol], ids: [e.id] });
};

/** A kill of the volley: the fifth of one is the bow's test. */
const volleyKill = (s: BattleState, h: Hero, vol: number): void => {
  const q = kept(h);
  if (q.vol !== vol) return;
  q.volK = (q.volK ?? 0) + 1;
  if (q.volK === FEAT.need.volleyKills) featPieceTest(s, h, 'hunterBow');
};

const arrowJump = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, vol = 0] = d.n ?? [];
  const f = fx('hunterBow', h);
  const o = nearestBut(s, d.x, d.y, f.range as number, d.ids ?? []);
  if (!o) return;
  launch(s, { kind: 'arrow', x: d.x, y: d.y, tx: o.x, ty: o.y, target: o.id, speed: HEROES.ranger.projectileSpeed, damage: mulBp(dmg, f.jumpBp as number), splash: 0, ignoresArmor: false, pierce: true, enchant: null, stun: 0, pid: h.pid, hostile: false, uq: -vol });
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: o.x, ty: o.y, kind: 'arrowJump' });
  featUse(s, h, 'hunterBow', 1);
};

const arrowAgain = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, vol = 0] = d.n ?? [];
  const o = nearestBut(s, d.x, d.y, SKILLS.multishot.radius, []);
  if (!o) return;
  damageEnemy(s, o, dmg, { pid: h.pid, pierce: true });
  ev(s, { t: 'boom', x: o.x, y: o.y, r: 400, kind: 'arrowAgain' });
  if (o.hp <= 0) volleyKill(s, h, vol);
};

/* ---------- Arco del rastreador (Flecha envenenada) ---------- */

/** The poisoned arrows: the `stacks`-th on one still poisoned by this ranger bursts in a cloud. */
export const trackerArrows = (s: BattleState, h: Hero, hits: Enemy[], ticks: number): void => {
  if (!hasUq(h, 'trackerBow')) return;
  const f = fx('trackerBow', h);
  for (const o of hits) {
    const q = marks(o);
    const n = q.tbBy === h.id && (q.tbUntil ?? 0) > s.tick ? (q.tb ?? 0) + 1 : 1;
    q.tbBy = h.id;
    q.tbUntil = s.tick + ticks;
    q.tb = n;
    if (n < (f.stacks as number)) continue;
    q.tb = 0;
    poisonCloud(s, h, o, f);
  }
};

const poisonCloud = (s: BattleState, h: Hero, at: Enemy, f: Record<string, number>): void => {
  const fever = formFinal(h, 'trackerBow') ? formFx('trackerBow', 'final') : null;
  const dmg = mulBp(at.dotDmg ?? 0, f.cloudBp as number);
  const every = at.dotEvery ?? 6;
  const ticks = at.dotTicks ?? 0;
  let n = 0;
  for (const o of enemiesNear(s, at.x, at.y, f.radius as number, (e) => targetable(s, e) && e.kind !== 'boneWall')) {
    poison(o, dmg, every, ticks, h.pid, at.dotType ?? 'poison');
    if (fever) weaken(o, fever.bp as number, fever.ticks as number);
    n++;
  }
  ev(s, { t: 'boom', x: at.x, y: at.y, r: f.radius as number, kind: 'poisonCloud' });
  featUse(s, h, 'trackerBow', 1);
  if (n >= FEAT.need.arrowCloud) featPieceTest(s, h, 'trackerBow');
};

/* ---------- Arco del despensero (Retroceso) ---------- */

/** On the leap, an arrow at each nightmare within reach (`max` at most, the nearest first); the kills are counted. */
export const leapArrows = (s: BattleState, h: Hero): void => {
  if (!hasUq(h, 'pantryBow')) return;
  const f = fx('pantryBow', h);
  const near = enemiesNear(s, h.x, h.y, f.range as number, (o) => targetable(s, o) && o.kind !== 'boneWall');
  near.sort((a, b) => dist2(a.x, a.y, h.x, h.y) - dist2(b.x, b.y, h.x, h.y) || a.id - b.id);
  const dmg = mulBp(mulBp(h.stats.attack, h.stats.powerBp), f.damageBp as number);
  let kills = 0;
  let n = 0;
  for (const o of near.slice(0, f.max as number)) {
    damageEnemy(s, o, dmg, { pid: h.pid, type: 'physical' });
    ev(s, { t: 'beam', x: h.x, y: h.y, tx: o.x, ty: o.y, kind: 'leapArrow' });
    n++;
    if (o.hp <= 0) kills++;
  }
  featUse(s, h, 'pantryBow', n);
  if (kills >= FEAT.need.leapKills) featPieceTest(s, h, 'pantryBow');
};

/** Salto a tiempo (the bow's final form): leaping low, the skill comes back at half its wait; −1 otherwise. */
export const timelyLeap = (h: Hero, sk: SkillDef): number => {
  if (!formFinal(h, 'pantryBow')) return -1;
  const f = formFx('pantryBow', 'final');
  if (h.hp * 10000 >= h.stats.maxHp * (f.belowBp as number)) return -1;
  return mulBp(mulBp(mulBp(sk.cooldown, h.stats.cooldownBp), uqBp(h, sk.id, 'cooldownBp')), f.cdBp as number);
};

/* ---------- Arco de la cantera (Disparo perforante) ---------- */

/**
 * At its line's end the arrow turns (to the side with more nightmares on it; the left on a tie) and flies on, `lengthBp`
 * as far and `damageBp` as hard, through what the first line didn't pierce. Twice, with Dos desvíos.
 */
export const deflect = (s: BattleState, h: Hero, ex: number, ey: number, len: number, w: number, dmg: number, hits: Enemy[]): void => {
  if (!hasUq(h, 'quarryBow')) return;
  const f = fx('quarryBow', h);
  const seen = hits.map((o) => o.id);
  const far = mulBp(len, f.lengthBp as number);
  const blow = mulBp(dmg, f.damageBp as number);
  let dir = iatan2(ey - h.y, ex - h.x);
  let x = ex;
  let y = ey;
  let n = 0;
  for (let turn = formFinal(h, 'quarryBow') ? 2 : 1; turn > 0; turn--) {
    let best: { a: number; tx: number; ty: number; on: Enemy[] } | null = null;
    for (const sgn of [1, -1]) {
      const a = (dir + sgn * (f.angle as number) + 1024) % 1024;
      const tx = x + idiv(cosA(a) * far, 10000);
      const ty = y + idiv(sinA(a) * far, 10000);
      const on = enemiesInLine(s, x, y, tx, ty, w).filter((o) => !seen.includes(o.id) && o.kind !== 'boneWall');
      if (!best || on.length > best.on.length) best = { a, tx, ty, on };
    }
    const b = best as { a: number; tx: number; ty: number; on: Enemy[] };
    for (const o of b.on) {
      damageEnemy(s, o, blow, { pid: h.pid, pierce: true });
      seen.push(o.id);
      n++;
    }
    ev(s, { t: 'beam', x, y, tx: b.tx, ty: b.ty, kind: 'deflect' });
    x = b.tx;
    y = b.ty;
    dir = b.a;
  }
  featUse(s, h, 'quarryBow', n);
  if (hits.length + n >= FEAT.need.pierceTwo) featPieceTest(s, h, 'quarryBow');
};

/* ---------- Jubón del halconero (Halcón) ---------- */

/** Every few seconds the hawk dives on its prey and stuns it (a piece's stun); the third on one elite is watched. */
export const hawkDive = (s: BattleState, b: Bot, o: Hero, prey: Enemy): void => {
  if (!hasUq(o, 'falconerJerkin')) return;
  const q = kept(o);
  if ((q.hawkAt ?? 0) > s.tick) return;
  const f = fx('falconerJerkin', o);
  q.hawkAt = s.tick + (f.every as number);
  const st = pieceStun(prey, f.stun as number);
  if (st > prey.stun) prey.stun = st;
  ev(s, { t: 'beam', x: b.x, y: b.y, tx: prey.x, ty: prey.y, kind: 'hawkDive' });
  if (st <= 0) return;
  featUse(s, o, 'falconerJerkin', 1);
  if (!prey.elite) return;
  const m = marks(prey);
  if (m.hkBy !== o.id) m.hk = 0;
  m.hkBy = o.id;
  m.hk = (m.hk ?? 0) + 1;
  if (m.hk === FEAT.need.hawkStuns) featPieceWatch(s, o, 'falconerJerkin', [prey.id], FEAT.watch.hawkElite, 1);
};

/** Halcón que vuelve (the jerkin's final form): its prey dead, the hawk takes the biggest in reach at once. */
export const hawkReturns = (s: BattleState, o: Hero, inReach: (e: Enemy) => boolean): Enemy | undefined => {
  if (!formFinal(o, 'falconerJerkin')) return undefined;
  let best: Enemy | undefined;
  for (const e of s.enemies) if (e.hp > 0 && inReach(e) && (!best || e.maxHp > best.maxHp || (e.maxHp === best.maxHp && e.id < best.id))) best = e;
  return best;
};

/* ---------- Coleto de la señalera (Bengala) ---------- */

/** The ranger whose flare brings flyers down (the signaler's coat), or none. */
export const flareOwner = (s: BattleState, z: Zone): Hero | undefined => s.heroes.find((h) => h.pid === z.pid && hasUq(h, 'signalerCoat'));

/** Under the flare: a flyer comes down while it burns (each counted once a flare; the fourth is the coat's test). */
export const flareGround = (s: BattleState, g: Hero, z: Zone, e: Enemy): void => {
  if (!groundable(e)) return;
  const t = fx('signalerCoat', g).ticks as number;
  if ((e.grounded ?? 0) < t) e.grounded = t;
  const m = marks(e);
  m.fl = g.id;
  if (m.flZ === z.id) return;
  m.flZ = z.id;
  const ids = (z.uqIds ??= []);
  ids.push(e.id);
  featUse(s, g, 'signalerCoat', 1);
  if (ids.length === FEAT.need.flareFly) featPieceTest(s, g, 'signalerCoat');
};

/* ---------- Jubón del trampero (Cepo) ---------- */

/** The ranger whose snares hold elites and flyers (the trapper's jerkin), or none. */
export const trapOwner = (s: BattleState, z: Zone): Hero | undefined => s.heroes.find((h) => h.pid === z.pid && hasUq(h, 'trapperJerkin'));

/** Whether the trapper's snare takes this one: a ground one as always, and a flyer too (never the big ones). */
export const trapTakes = (e: Enemy): boolean => !isFlying(e) || groundable(e);

/** The trapper's snare shut on `e`: an elite held `eliteBp` of it (a boss, never), a flyer brought down; Cepo que arrastra. */
export const trapShut = (s: BattleState, g: Hero, z: Zone, e: Enemy): number => {
  const f = fx('trapperJerkin', g);
  const hold = z.stun ?? 0;
  const st = e.elite ? (bigOne(e) ? 0 : stunFor({ ...e, elite: false }, mulBp(hold, f.eliteBp as number))) : stunFor(e, hold);
  if (isFlying(e) && st > 0 && (e.grounded ?? 0) < st) e.grounded = st;
  if (e.elite && st > 0) {
    featUse(s, g, 'trapperJerkin', 1);
    featPieceWatch(s, g, 'trapperJerkin', [e.id], imin(st, FEAT.watch.trap), 1);
  }
  if (formFinal(g, 'trapperJerkin')) {
    const ff = formFx('trapperJerkin', 'final');
    for (const o of enemiesNear(s, e.x, e.y, ff.radius as number, (x) => x.id !== e.id && targetable(s, x))) {
      const d = isqrt(dist2(o.x, o.y, e.x, e.y));
      if (d > 300) shove(s, o, e.x, e.y, -imin(ff.pull as number, d - 300));
    }
    ev(s, { t: 'boom', x: e.x, y: e.y, r: ff.radius as number, kind: 'snareDrag' });
  }
  return st;
};

/* ---------- Punta de la gran balista (Flecha del titán) ---------- */

/** Through each elite it pierced, the arrow sheds splinters at the nearest around it; Flecha que vuelve, later. */
export const splinters = (s: BattleState, h: Hero, hits: Enemy[], dmg: number, sx: number, sy: number, ex: number, ey: number, w: number): void => {
  if (!hasUq(h, 'ballistaTip')) return;
  const f = fx('ballistaTip', h);
  const blow = mulBp(dmg, f.damageBp as number);
  let dealt = 0;
  let elites = 0;
  for (const el of hits) {
    if (!el.elite) continue;
    elites++;
    const near = enemiesNear(s, el.x, el.y, f.reach as number, (o) => o.id !== el.id && targetable(s, o) && o.kind !== 'boneWall');
    near.sort((a, b) => dist2(a.x, a.y, el.x, el.y) - dist2(b.x, b.y, el.x, el.y) || a.id - b.id);
    for (const o of near.slice(0, f.splinters as number)) {
      dealt += damageEnemy(s, o, blow, { pid: h.pid, type: 'physical' });
      ev(s, { t: 'beam', x: el.x, y: el.y, tx: o.x, ty: o.y, kind: 'splinter' });
    }
  }
  featUse(s, h, 'ballistaTip', dealt);
  if (elites >= FEAT.need.titanElites) featPieceTest(s, h, 'ballistaTip');
  if (formFinal(h, 'ballistaTip')) {
    const ff = formFx('ballistaTip', 'final');
    due(s, { at: s.tick + (ff.after as number), k: 'arrowBack', hid: h.id, x: ex, y: ey, n: [mulBp(dmg, ff.damageBp as number), w, sx, sy] });
  }
};

const arrowBack = (s: BattleState, h: Hero, d: UqDue): void => {
  const [dmg = 0, w = 0, sx = 0, sy = 0] = d.n ?? [];
  for (const o of enemiesInLine(s, d.x, d.y, sx, sy, w)) if (o.kind !== 'boneWall') damageEnemy(s, o, dmg, { pid: h.pid, ignoresArmor: true });
  ev(s, { t: 'beam', x: d.x, y: d.y, tx: sx, ty: sy, kind: 'titanArrowBack' });
};

/* ---------- Pluma del tirador (Tirador oculto) ---------- */

/** The hiding begins: how much the kills may lengthen it (up to `capBp` of it). */
export const hiddenGrows = (h: Hero): void => {
  if (!hasUq(h, 'archerFeather')) return;
  const q = kept(h);
  q.hidCap = mulBp(h.hidden ?? 0, fx('archerFeather', h).capBp as number) - (h.hidden ?? 0);
  q.hidAdd = 0;
  q.hidOn = 1;
};

/* ---------- Lente del afinador (Ojo de halcón) ---------- */

/** The eye opens: its kills are counted afresh. */
export const eyeOpens = (h: Hero): void => {
  if (!hasUq(h, 'tunerLens')) return;
  const q = kept(h);
  q.eyeK = 0;
  q.eyeE = 0;
  q.eyeDone = 0;
};

/** A kill by the ranger (heroes.ts onKill): the feather lengthens the hiding, the lens counts the eye's kills. */
export const rangerKill = (s: BattleState, h: Hero, e: Enemy | undefined): void => {
  const q = h.uqT as Record<string, number>;
  if (q.hidOn && (h.hidden ?? 0) > 0) {
    featUse(s, h, 'archerFeather', 1);
    const add = imin(fx('archerFeather', h).perKill as number, (q.hidCap ?? 0) - (q.hidAdd ?? 0));
    if (add > 0) {
      h.hidden = (h.hidden ?? 0) + add;
      q.hidAdd = (q.hidAdd ?? 0) + add;
      if (q.hidAdd >= (q.hidCap ?? 0)) featPieceTest(s, h, 'archerFeather');
    }
  }
  if ((h.eagle ?? 0) > 0 && q.eyeK !== undefined && !q.eyeDone) {
    q.eyeK++;
    if (e?.elite) q.eyeE = 1;
    if (q.eyeK >= FEAT.need.eyeKills && q.eyeE) {
      q.eyeDone = 1;
      featPieceTest(s, h, 'tunerLens');
    }
  }
};

/** A basic attack's extra criticals from a piece: the flare's grounded (Aire quemado), a guiding eye's target (Ojo que guía). */
export const pieceCrit = (s: BattleState, h: Hero, e: Enemy): boolean => {
  const m = e.uqE as Record<string, number>;
  if (m.fl === h.id && (e.grounded ?? 0) > 0 && formFinal(h, 'signalerCoat') && rngChance(s.rng, formFx('signalerCoat', 'final').critBp as number)) return true;
  return (m.gd ?? 0) > s.tick && m.gdBy !== h.id && rngChance(s.rng, formFx('tunerLens', 'final').critBp as number);
};

/** Último disparo (the feather's final form): the next basic attack after the hiding strikes harder. */
export const lastShot = (h: Hero, dmg: number): number => {
  const q = h.uqT as Record<string, number>;
  if (!q.last) return dmg;
  q.last = 0;
  return mulBp(dmg, formFx('archerFeather', 'final').mulBp as number);
};

/**
 * A critical basic attack (`dmg` before it was critical): with the lens, while the eye is open, one on an elite or a
 * boss looses an arrow more at the nearest other (not a basic attack: it doesn't crit); Ojo que guía marks the target.
 */
export const critExtras = (s: BattleState, h: Hero, e: Enemy, dmg: number): void => {
  if (!hasUq(h, 'tunerLens')) return;
  if (formFinal(h, 'tunerLens')) {
    const m = marks(e);
    m.gd = s.tick + (formFx('tunerLens', 'final').ticks as number);
    m.gdBy = h.id;
  }
  if (!((h.eagle ?? 0) > 0) || !(e.elite || isBoss(e) || !!NIGHTMARES[e.kind].big)) return;
  const f = fx('tunerLens', h);
  const o = nearestBut(s, e.x, e.y, f.range as number, [e.id]);
  if (!o) return;
  launch(s, { kind: 'arrow', x: h.x, y: h.y, tx: o.x, ty: o.y, target: o.id, speed: HEROES.ranger.projectileSpeed, damage: mulBp(dmg, f.damageBp as number), splash: 0, ignoresArmor: false, pierce: false, enchant: null, stun: 0, pid: h.pid, hostile: false });
  featUse(s, h, 'tunerLens', 1);
};

/* ---------- each tick, and the queue ---------- */

/** Every tick, for a ranger wearing a piece that keeps something: the hiding's end (Último disparo). */
export const rangerHeroTick = (_s: BattleState, h: Hero): void => {
  const q = h.uqT as Record<string, number>;
  if (q.hidOn && !((h.hidden ?? 0) > 0)) {
    q.hidOn = 0;
    if (formFinal(h, 'archerFeather')) q.last = 1;
  }
};

/** The ranger's later effects; false if `d` isn't one. */
export const rangerDue = (s: BattleState, h: Hero, d: UqDue): boolean => {
  if (d.k === 'arrowJump') arrowJump(s, h, d);
  else if (d.k === 'arrowAgain') arrowAgain(s, h, d);
  else if (d.k === 'arrowBack') arrowBack(s, h, d);
  else return false;
  return true;
};
